import React, { useMemo, useCallback, useState, useContext, useEffect } from 'react';
import { BrandLogo } from '../components/BrandLogo';
import { BrandTitleSection } from '../components/BrandTitleSection';
import { ECommBrand } from 'common/api/e-comm/models/ECommBrand';
import { Link } from 'react-router-dom';
import { Product, ProductVariant, ProductType } from 'common/api/e-comm/models/Product';
import { ProductDetailTerms, getGeneralInformationFilter } from '../ProductDetailTerms';
import { BarCode, PhysicalCard } from '../../../components/custom-icons';
import { ReloadIcon } from '@radix-ui/react-icons';
import { Btn } from 'components/btn/Btn';
import { getUrlToLoginPage, sendToLoginPage } from 'features/auth/components/withAuth';
import { Routes } from 'routes';
import { useStateRebateAdjustment } from 'common/hooks/useStateRebateAdjustment';
import { useContentfulGlobalContent } from 'common/api/contentful/global';
import { EntryFields } from 'contentful';
import { UserContent } from 'components/user-content/UserContent';
import Skeleton from 'react-loading-skeleton';
import * as Tabs from '@radix-ui/react-tabs';
import { FadeIn } from 'components/loading-screen/FadeIn';
import { OfferTab } from 'features/product/ProductDetailMainTab';
import { Helmet } from 'react-helmet-async';
import { useRootSelector } from 'rootStore';
import { selectIsAuthenticated } from 'common/features/store/duck/account/duck';

interface UnAuthProductDetailsViewProps {
    brand?: ECommBrand;
    products?: Product[];
}

const UnAuthPDPContext = React.createContext<Pick<UnAuthProductDetailsViewProps, 'products'>>({
    products: [],
});

const useAffiliateUrlProtection = () => {
    const isAffiliateRoute =
        window.location.pathname.includes('affiliate-') ||
        window.location.pathname.includes('/shop-online');
    const isLoggedIn = useRootSelector(selectIsAuthenticated);

    useEffect(() => {
        if (isAffiliateRoute && !isLoggedIn) {
            sendToLoginPage(window.location.pathname);
        }
    }, [isAffiliateRoute, isLoggedIn]);
};

export const UnAuthProductDetailsView = ({
    brand,
    products = [],
}: UnAuthProductDetailsViewProps) => {
    useAffiliateUrlProtection();
    const content = useContentfulGlobalContent();

    const canonicalUrl =
        window.location.href.replace('/gift-cards', '').split('?')[0] + '/gift-cards';

    // CASE: Brand is not loaded yet -- show loading screen
    if (!brand) {
        return <Skeleton className="w-full" style={{ height: '100vh' }} />;
    }

    /**
     * prerender override documented here
     * https://docs.prerender.io/docs/25-empty-or-partially-rendered-pages
     */
    if (brand && products.length > 0 && content) {
        setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).prerenderReady = true;
        }, 2000);
    }

    return (
        <div className="ml-auto mr-auto max-w-3xl mt-4 px-6 md:px-0 text-left">
            <Helmet>
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            <div>
                <HeaderSection
                    brand={brand}
                    products={products}
                    description={content?.unauthPdpBrandDescription}
                />
                <Content products={products} brand={brand} />
                <Footer brandId={brand?.id} productType={products[0]?.type} />
            </div>
        </div>
    );
};

export const useMaxProductRebate = () => {
    const {
        stateRebateAdjustment: { adjustment: adjustLocalProduct },
    } = useStateRebateAdjustment(true);

    const {
        stateRebateAdjustment: { adjustment: adjustNonLocalProduct },
    } = useStateRebateAdjustment(false);

    const calculateProductRebate = useCallback(
        (product: Product) => {
            const adjustment = product.isLocalProduct ? adjustLocalProduct : adjustNonLocalProduct;

            let max_rebate = 0;
            let isOnBonus = false;

            if (product.type !== ProductType.Reload) {
                // NOTE: canculates the __max_rebate__ for this product
                product.variants.forEach((variant: ProductVariant) => {
                    // skip the variant if it's not available or visible
                    if (variant.isAvailable && variant.isVisible) {
                        // NOTE: Default the rebate to be the variant's rebate
                        let variant_rebate = variant.rebate;

                        // CASE: variant is on bonus -- set that to be the variant_rebate
                        if (variant.bonus) {
                            isOnBonus = true;
                            variant_rebate = variant.bonus.rebate;
                        }

                        // CASE: we shoud see if the max_rebate less than variant_rebate
                        if (max_rebate < variant_rebate) {
                            max_rebate = variant_rebate;
                        }
                    }
                });
            }

            return {
                rebate: max_rebate - adjustment,
                isOnBonus,
            };
        },
        [adjustLocalProduct, adjustNonLocalProduct]
    );

    return { calculateProductRebate };
};

export const productWithHighestRebate = (
    products: Product[],
    calcProductRebate: (_: Product) => { rebate: number; isOnBonus: boolean }
) => {
    let bestProduct: Product | undefined;
    let bestRebate = 0;
    let onBonus = false;

    products.forEach((product: Product) => {
        const { rebate, isOnBonus } = calcProductRebate(product);

        if (bestRebate <= rebate) {
            bestRebate = rebate;
            bestProduct = product;
            onBonus = isOnBonus;
        }
    });
    return {
        bonusRebate: onBonus ? bestRebate : undefined,
        rebate: Math.max(
            ...(bestProduct?.variants || []).map((variant: ProductVariant) => variant.rebate)
        ),
        product: bestProduct,
    };
};

type HeaderSectionProps = { description?: EntryFields.RichText } & UnAuthProductDetailsViewProps;
const HeaderSection = (props: HeaderSectionProps) => {
    const { calculateProductRebate } = useMaxProductRebate();
    const { rebate, bonusRebate } = useMemo(() => {
        return productWithHighestRebate(props.products || [], calculateProductRebate);
    }, [props.products, calculateProductRebate]);

    return (
        <div className="flex gap-4 sm:gap-8">
            <BrandLogo brand={props.brand} />
            <BrandTitleSection brand={props.brand} rebate={rebate} bonusRebate={bonusRebate}>
                {props.description && (
                    <div className="pt-3 text-sm text-grey-1">
                        <UserContent
                            content={props.description}
                            data={{ brand: props.brand?.name }}
                        />
                    </div>
                )}
            </BrandTitleSection>
        </div>
    );
};

const Content = ({ products, brand }: { products?: Product[]; brand?: ECommBrand }) => (
    <UnAuthPDPContext.Provider value={{ products }}>
        <div className="mt-6 mb-10 sm:my-10 text-grey-2">
            <p className="mb-2">Available Card Types:</p>
            <ProductNav brand={brand} />
            <ControlButtons />
        </div>
    </UnAuthPDPContext.Provider>
);

const ControlButtons = () => {
    const returnUrl = window.location.pathname;
    const url = getUrlToLoginPage(returnUrl);

    const goToLogin = () => {
        sendToLoginPage(returnUrl);
    };

    return (
        <div className="mt-8">
            <div>
                <Btn className="block mx-auto" onClick={goToLogin}>
                    Sign In to Add to Cart
                </Btn>
                <a className="hidden" title="Login" href={url}>
                    login
                </a>
            </div>
            <div className="flex flex-col mt-8 gap-3">
                <p className="text-center text-grey-2 text-xs">Don't have account?</p>
                <Link className="font-semibold mx-auto" to={Routes.EnrollWithoutEnrollCode}>
                    Enroll Here
                </Link>
            </div>
        </div>
    );
};

const ProductNav = ({ brand }: { brand?: ECommBrand }) => {
    const { products } = useContext(UnAuthPDPContext);
    const products_ = products ?? [];
    const [selectedTab, setSelectedTab] = useState<ProductType>(ProductType.GiftCard);
    const eGiftProduct = hasProductType(products_, ProductType.EGiftCard);
    const physicalProduct = hasProductType(products_, ProductType.GiftCard);
    const reloadProduct = hasProductType(products_, ProductType.Reload);

    const onTabSelection = (tab: string) => {
        setSelectedTab(tab as unknown as ProductType);
    };

    // Side-Effect: compute what the initial selected tab should be
    useEffect(() => {
        if (products && products.length > 0) {
            if (products.find((p) => p.type === ProductType.GiftCard)) {
                return setSelectedTab(ProductType.GiftCard);
            }
            if (products.find((p) => p.type === ProductType.EGiftCard)) {
                return setSelectedTab(ProductType.EGiftCard);
            }
            if (products.find((p) => p.type === ProductType.Reload)) {
                return setSelectedTab(ProductType.Reload);
            }
        }
    }, [products]);

    return (
        <Tabs.Root value={selectedTab} onValueChange={onTabSelection}>
            <Tabs.List
                className={`pdp_available_as_bar flex flex-shrink-0 border-b border-solid border-grey-3`}
            >
                {physicalProduct && (
                    <OfferTab
                        product={physicalProduct}
                        selected={selectedTab === ProductType.GiftCard}
                        tabId={ProductType.GiftCard}
                        title="Physical"
                        icon={<PhysicalCard height="1.25rem" className="inline-block mr-2" />}
                    />
                )}
                {eGiftProduct && (
                    <OfferTab
                        product={eGiftProduct}
                        title="eGift"
                        icon={<BarCode height="1rem" className="inline-block mr-2" />}
                        tabId={ProductType.EGiftCard}
                        selected={selectedTab === ProductType.EGiftCard}
                        className=""
                    />
                )}
                {reloadProduct && (
                    <OfferTab
                        product={reloadProduct}
                        selected={selectedTab === ProductType.Reload}
                        tabId={ProductType.Reload}
                        title="Reload"
                        icon={<ReloadIcon height="1.25rem" className="inline-block mr-2" />}
                    />
                )}
            </Tabs.List>
            <TabsContent selectedTab={selectedTab} brand={brand} />
        </Tabs.Root>
    );
};

const TabsContent = ({ selectedTab, brand }: { selectedTab: ProductType; brand?: ECommBrand }) => {
    return (
        <div
            data-cnstrc-product-detail
            data-cnstrc-item-id={brand?.id}
            data-cnstrc-item-name={brand?.name}
        >
            <FadeIn when={selectedTab === ProductType.EGiftCard}>
                <EGiftContent />
            </FadeIn>
            <FadeIn when={selectedTab === ProductType.GiftCard}>
                <PhysicalContent />
            </FadeIn>
            <FadeIn when={selectedTab === ProductType.Reload}>
                <ReloadContent />
            </FadeIn>
        </div>
    );
};

const hasProductType = (products: Product[], productType: ProductType): Product | undefined => {
    return products.find((p: Product) => p.type === productType);
};

const EGiftContent = () => {
    const { products } = useContext(UnAuthPDPContext);
    const eGiftProduct = hasProductType(products ?? [], ProductType.EGiftCard);

    if (!eGiftProduct) {
        return null;
    }

    return (
        <div>
            <ul className="list-disc list-outside ml-4 mt-3">
                <li>
                    Available in amounts between ${eGiftProduct.minValue}-${eGiftProduct.maxValue}
                </li>

                <li>
                    Digital gift cards that are delivered electronically and automatically saved to
                    your Wallet.
                </li>

                <li>
                    Accessible right from your phone — most can easily be scanned by the retailer
                    for quick check-out.
                </li>

                <li>
                    Can be sent electronically to be used for gifting. Schedule for planned
                    occasions you don’t want to forget or send immediately as an eGift at time of
                    purchase.
                </li>
            </ul>
        </div>
    );
};

const getPhysicalProductMinMax = (product: Product): [null | boolean, number[]] => {
    // CASE: for some reason product-variants are not filled in
    if (product.variants.length === 0) {
        return [null, []];
    }

    // CASE: we have some variants
    const values = product.variants
        .reduce((acc: number[], variant: ProductVariant) => {
            if (variant.isAvailable && variant.isVisible) {
                acc.push(variant.price);
            }
            return acc;
        }, [])
        .sort((n, n2) => n - n2);

    let min = 0;
    let max = 0;
    let hasMinMax = false;

    // CASE: We have a min and max(at least 2 values)
    if (values.length > 1) {
        min = values[0];
        max = values[values.length - 1];
        hasMinMax = true;
    } else if (values.length === 1) {
        // CASE: We only have a single value(min === max)
        min = values[0];
    }

    return [hasMinMax, [min, max]];
};

const PhysicalContent = () => {
    const { products } = useContext(UnAuthPDPContext);
    const physicalProduct = hasProductType(products ?? [], ProductType.GiftCard);

    if (!physicalProduct) {
        return null;
    }

    const [hasMinMax, [min, max]] = getPhysicalProductMinMax(physicalProduct);
    const renderAvailableAmounts = () => {
        if (hasMinMax === null) {
            return null;
        } else if (!hasMinMax) {
            return <li>Available as ${min}</li>;
        } else if (hasMinMax) {
            return (
                <li>
                    Available in amounts between ${min}-${max}
                </li>
            );
        }
    };

    return (
        <div>
            <ul className="list-disc list-outside ml-4 mt-3">
                {renderAvailableAmounts()}
                <li>
                    Plastic or paper-based gift cards that are shipped to your organization or can
                    be mailed direct to your home address using our Ship to Home option.
                </li>

                <li>
                    Make great gifts for everyone in your life, with multiple denomination options
                    to choose from.
                </li>

                <li>Can help serve as a visual reminder that you have funds available to spend.</li>

                <li>
                    A great payment alternative when eGift cards aren’t available for a specific
                    brand.
                </li>
            </ul>
        </div>
    );
};

const ReloadContent = () => {
    const { products } = useContext(UnAuthPDPContext);
    const reloadProduct = hasProductType(products ?? [], ProductType.Reload);

    if (!reloadProduct) {
        return null;
    }

    return (
        <div>
            <ul className="list-disc list-outside ml-4 mt-3">
                <li>
                    Available in amounts between ${reloadProduct.minValue}-$
                    {reloadProduct.maxBalance}
                </li>

                <li>
                    Allows you to electronically reload funds onto physical gift cards purchased
                    from RaiseRight — available for ~125 brands.
                </li>

                <li>
                    Great for regular purchases like gas and groceries, or the daily visits to your
                    favorite coffee shop or restaurant drive-thru.
                </li>

                <li>
                    Can be saved to your account ‘Wallet’ and reloaded right in the mobile app or on
                    the website to keep track of your balances.
                </li>

                <li>Saves time and card waste.</li>
            </ul>
        </div>
    );
};

const Footer = ({ brandId, productType }: { brandId?: number; productType?: ProductType }) => {
    return brandId && productType ? (
        <div className="mt-24">
            <ProductDetailTerms
                brandId={brandId}
                productType={productType}
                defaultExpandedFilter={getGeneralInformationFilter}
            />
        </div>
    ) : null;
};
